/**
 * グループ画面。支出・精算・メンバー・設定をタブで切り替える。
 */
import { createRecentGroupStore, toGroupStats, useGroup } from "@spli/app-logic";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useParams } from "react-router-dom";
import { ShareOverlay } from "../components/ShareOverlay.js";
import { ErrorView, LoadingView } from "../components/StatusViews.js";
import { ExpensesView } from "./group/ExpensesView.js";
import { GroupSettings } from "./group/GroupSettings.js";
import { MembersView } from "./group/MembersView.js";
import { SettlementView } from "./group/SettlementView.js";
import { Icon } from "../components/Icon.js";

export function GroupPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const groupQuery = useGroup(shareToken);
  const recentStore = useMemo(() => createRecentGroupStore(), []);
  const [editingSettings, setEditingSettings] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const group = groupQuery.data;

  // この端末の「最近開いたグループ」に記録する (会員登録なしでも履歴が残る)
  useEffect(() => {
    if (group) recentStore.remember({ shareToken: group.shareToken, name: group.name });
  }, [group, recentStore]);

  if (groupQuery.isPending) return <LoadingView />;
  // 共有 URL が間違っていた場合、行き止まりにせずトップへ戻せるようにする
  if (groupQuery.isError || !group) {
    return (
      <>
        <ErrorView error={groupQuery.error ?? new Error("グループが見つかりません")} />
        <p className="muted" style={{ marginTop: 16, textAlign: "center" }}>
          URL を確認するか、<Link to="/">トップから新しいグループを作成</Link>してください。
        </p>
      </>
    );
  }

  const stats = toGroupStats(group);
  const shareUrl = `${window.location.origin}/g/${group.shareToken}`;


  return (
    <>
      <section className="card">
        {/*
          イベント名と操作を同じ行に置く。名前が長いときは
          操作のまとまりごと次の行へ送る (group-actions は折り返さない)。
          「設定」だけが折り返されて 1 文字ずつ縦に並ぶのを避けるため。
        */}
        <div className="card__title card__title--lg">
          <h1 className="group-name">{group.name}</h1>
          <span className="group-actions">
            {/* 招待の導線。リンクは常に見せず、押したときだけ出す */}
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => setShowShare(true)}
            >
              <Icon name="share" size={16} /> 共有リンクを表示
            </button>
            {/* 設定はタブを 1 つ使うほど頻繁ではないので、イベント名の隣から開く */}
            <button
              type="button"
              className="button button--ghost button--small"
              aria-expanded={editingSettings}
              onClick={() => setEditingSettings((open) => !open)}
            >
              {editingSettings ? (
                "閉じる"
              ) : (
                <>
                  <Icon name="settings" size={16} /> 設定
                </>
              )}
            </button>
          </span>
        </div>
        {/* 人数もこの行にまとめる。見出しの隣にバッジで置くと唐突に見えるため */}
        <p className="muted">
          参加 {stats.memberCount} 人 ・ 支出 {stats.expenseCount} 件 ・ 合計 {stats.totalLabel} ・
          1人あたり {stats.perPersonLabel}
        </p>

        {editingSettings && (
          <GroupSettings
            group={group}
            onSaved={() => setEditingSettings(false)}
            onCancel={() => setEditingSettings(false)}
          />
        )}

      </section>

      {showShare && (
        <ShareOverlay url={shareUrl} title={group.name} onClose={() => setShowShare(false)} />
      )}

      {/* 使う順 (メンバーを決める → 支出を記録する → 精算する) に並べる */}
      <nav className="tabs">
        <TabLink to={`/g/${group.shareToken}/members`}>メンバー</TabLink>
        <TabLink to={`/g/${group.shareToken}`} end>
          支出
        </TabLink>
        <TabLink to={`/g/${group.shareToken}/settlement`}>精算</TabLink>
      </nav>

      <Routes>
        <Route index element={<ExpensesView group={group} />} />
        <Route path="settlement" element={<SettlementView group={group} />} />
        <Route path="members" element={<MembersView group={group} />} />
        {/* 設定タブは廃止 (イベント名の隣から開く)。既存のリンクは支出へ送る */}
        <Route path="settings" element={<Navigate to={`/g/${group.shareToken}`} replace />} />
      </Routes>
    </>
  );
}

function TabLink({ to, end, children }: { to: string; end?: boolean; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `tabs__link${isActive ? " tabs__link--active" : ""}`}
    >
      {children}
    </NavLink>
  );
}
